import { CITY_FACTS, CityFact, CitySlug } from './cityFacts';
import { CITY_PRACTICAL_NOTES } from './cityPracticalNotes';
import { getMetierCityAdvice, METIER_FALLBACK_ADVICE } from './metierCityAdvice';
import { getCityMetierFaqPilot } from './cityMetierFaqPilots';

export type MetierCityContent = {
  localContext: string;
  /** FAQ locales enrichies (#799) — optionnel. */
  faq?: { q: string; a: string }[];
};

export type MetierSeoBlock = {
  slug: string;
  label: string;
  singular: string;
  buildLocalContext: (city: CityFact) => string;
};

function composeLocalContext(metierSlug: string, city: CityFact): string {
  const citySlug = city.slug as CitySlug;
  const cityNote = CITY_PRACTICAL_NOTES[citySlug];
  const advice =
    getMetierCityAdvice(metierSlug, citySlug) || METIER_FALLBACK_ADVICE[metierSlug]?.(city) || '';
  return [cityNote, advice].filter(Boolean).join('\n\n');
}

export const METIER_SEO_BLOCKS: Record<string, MetierSeoBlock> = {
  'educateur-canin': {
    slug: 'educateur-canin',
    label: 'Éducateur canin',
    singular: 'éducateur canin',
    buildLocalContext: city => composeLocalContext('educateur-canin', city),
  },
  'comportementaliste-canin': {
    slug: 'comportementaliste-canin',
    label: 'Comportementaliste canin',
    singular: 'comportementaliste canin',
    buildLocalContext: city => composeLocalContext('comportementaliste-canin', city),
  },
  'pet-sitter': {
    slug: 'pet-sitter',
    label: 'Pet-sitter',
    singular: 'pet-sitter',
    buildLocalContext: city => composeLocalContext('pet-sitter', city),
  },
  'promeneur-canin': {
    slug: 'promeneur-canin',
    label: 'Promeneur canin',
    singular: 'promeneur canin',
    buildLocalContext: city => composeLocalContext('promeneur-canin', city),
  },
  'toiletteur-canin': {
    slug: 'toiletteur-canin',
    label: 'Toiletteur canin',
    singular: 'toiletteur canin',
    buildLocalContext: city => composeLocalContext('toiletteur-canin', city),
  },
  'pension-canine': {
    slug: 'pension-canine',
    label: 'Pension canine',
    singular: 'pension canine',
    buildLocalContext: city => composeLocalContext('pension-canine', city),
  },
  'nutritionniste-animal': {
    slug: 'nutritionniste-animal',
    label: 'Nutritionniste animal',
    singular: 'nutritionniste animal',
    buildLocalContext: city => composeLocalContext('nutritionniste-animal', city),
  },
  'communication-animale': {
    slug: 'communication-animale',
    label: 'Communication animale',
    singular: 'communicant animal',
    buildLocalContext: city => composeLocalContext('communication-animale', city),
  },
  'loisir-canin': {
    slug: 'loisir-canin',
    label: 'Loisir canin',
    singular: 'activité loisir canin',
    buildLocalContext: city => composeLocalContext('loisir-canin', city),
  },
  'osteopathe-canin': {
    slug: 'osteopathe-canin',
    label: 'Ostéopathe canin',
    singular: 'ostéopathe canin',
    buildLocalContext: city => composeLocalContext('osteopathe-canin', city),
  },
  'massage-animalier': {
    slug: 'massage-animalier',
    label: 'Massage animalier',
    singular: 'massage animalier',
    buildLocalContext: city => composeLocalContext('massage-animalier', city),
  },
  'sport-canin': {
    slug: 'sport-canin',
    label: 'Sport canin',
    singular: 'moniteur de sport canin',
    buildLocalContext: city => composeLocalContext('sport-canin', city),
  },
  naturopathe: {
    slug: 'naturopathe',
    label: 'Naturopathe animalier',
    singular: 'naturopathe animalier',
    buildLocalContext: city => composeLocalContext('naturopathe', city),
  },
  'taxi-animalier': {
    slug: 'taxi-animalier',
    label: 'Taxi animalier',
    singular: 'taxi animalier',
    buildLocalContext: city => composeLocalContext('taxi-animalier', city),
  },
  'photographe-animalier': {
    slug: 'photographe-animalier',
    label: 'Photographe animalier',
    singular: 'photographe animalier',
    buildLocalContext: city => composeLocalContext('photographe-animalier', city),
  },
  'secourisme-animalier': {
    slug: 'secourisme-animalier',
    label: 'Secourisme animalier',
    singular: 'formation de secourisme animalier',
    buildLocalContext: city => composeLocalContext('secourisme-animalier', city),
  },
};

export const METIER_SLUGS = Object.keys(METIER_SEO_BLOCKS);

export function buildMetierCityEntry(metierSlug: string, citySlug: CitySlug): MetierCityContent {
  const block = METIER_SEO_BLOCKS[metierSlug];
  const city = CITY_FACTS[citySlug];
  if (!block || !city) {
    throw new Error(`Missing metier/city: ${metierSlug}/${citySlug}`);
  }
  const faq = getCityMetierFaqPilot(metierSlug, citySlug);
  return {
    localContext: block.buildLocalContext(city),
    ...(faq ? { faq } : {}),
  };
}

export function buildMetierDefault(metierSlug: string): MetierCityContent {
  return buildMetierCityEntry(metierSlug, 'paris');
}
